// ─────────────────────────────────────────────────────────────────────
// LES COTES DU BRUT SONT DÉRIVÉES DE CELLES DE LA PIÈCE
//
// Le serveur ne COMBLE que l'absence : « une cote saisie par le
// deviseur n'est jamais touchée ». La règle est juste pour une cote de
// brut posée à la main — et fausse pour une cote de brut DÉRIVÉE d'une
// cote de pièce qui vient de changer.
//
// Constaté le 27 août 2026 sur « Palier - Bearing » : pièce finie
// 535 × 142 × 135, brut 540 × 150 × **160**. La règle donne 140 —
// vérifié en rejouant `cotesBrut` sur ces cotes. Le 160 dérivait d'une
// épaisseur lue plus grande, corrigée depuis dans l'écran des cotes
// lues. La correction est arrivée dans `geometry`, et le brut est resté
// sur l'ancienne valeur, en silence, avec 14 % de volume en trop.
//
// ── CE QUE ÇA COÛTE, ET C'EST ASSUMÉ ──
//
// Un brut saisi À LA MAIN sur cette pièce est perdu au moment où on
// corrige une cote de la pièce. C'est le bon échange : on vient de dire
// que la pièce n'a pas les dimensions qu'on croyait, donc le brut qu'on
// avait choisi pour elle ne vaut plus. Et il se ressaisit juste en
// dessous.
// ─────────────────────────────────────────────────────────────────────

/** Les cinq cotes de brut, toutes dérivées de l'encombrement. */
export const COTES_BRUT = ['raw_l', 'raw_w', 'raw_h', 'raw_diameter', 'raw_length'];

/** Où ranger une cote lue — la case de `geometry.part` qu'elle remplit. */
export const CHAMP_DE_LA_CASE = {
  Longueur: 'l', Largeur: 'w', Profondeur: 'h', 'Ø': 'diameter',
};

/**
 * ── SUR UNE PIÈCE RONDE, LA PROFONDEUR EST LA LONGUEUR AXIALE ──
 *
 * L'écran affiche « Ø » et « Profondeur » pour une pièce de révolution,
 * et la profondeur y lit `part.length`. La saisie, elle, l'écrivait dans
 * `part.h` — une case que le calcul d'une pièce ronde ne lit pas. Devis
 * 2026-10-027, 22 octobre 2026 : Philippe tape 15,3, la valeur est bien
 * enregistrée, et rien ne change à l'écran. La case restait vide.
 */
export function champDeLaCase(piece, labelCase) {
  if (piece?.geometry?.partShape === 'cylinder' && labelCase === 'Profondeur') return 'length';
  return CHAMP_DE_LA_CASE[labelCase] ?? null;
}

/**
 * Le patch qui efface les cotes du brut, pour que le serveur les repose
 * à l'enregistrement à partir des variantes recalculées.
 */
export function effacerCotesBrut() {
  return Object.fromEntries(COTES_BRUT.map((c) => [c, null]));
}

export default effacerCotesBrut;

/**
 * Le patch complet quand le deviseur retient une cote lue.
 *
 * Il fait DEUX choses, et la seconde est celle qu'on oubliait : ranger
 * la valeur dans la case de la pièce, et effacer le brut qui en dérive.
 * Les séparer, c'est exactement le défaut du Palier — la cote corrigée
 * arrivait dans `geometry`, le brut restait sur l'ancienne.
 *
 * Rend `null` si la case n'existe pas : on ne range pas une cote dans
 * une case qu'on ne connaît pas.
 */
export function patchCoteRetenue(piece, labelCase, valeur) {
  const clef = champDeLaCase(piece, labelCase);
  if (!clef) return null;
  return {
    geometry: {
      ...piece?.geometry,
      part: { ...(piece?.geometry?.part ?? {}), [clef]: valeur },
    },
    ...effacerCotesBrut(),
  };
}
